import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Code, CheckCircle, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import api from '../api';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/payment');
      return;
    }

    pollPaymentStatus(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, searchParams, navigate]);

  const pollPaymentStatus = async (sessionId, currentAttempt = 0) => {
    if (currentAttempt >= maxAttempts) {
      setStatus('error');
      toast.error('Payment verification timed out. Please check your email or contact support.');
      return;
    }

    try {
      const response = await api.get(`/payment/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentDetails(response.data);
        toast.success('Payment successful!');
        
        // Create subscription automatically
        try {
          await api.post(`/subscriptions/create?session_id=${sessionId}`);
        } catch (error) {
          
        }
        
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error('Payment session expired');
        return;
      }

      // Continue polling
      setAttempts(currentAttempt + 1);
      setTimeout(() => pollPaymentStatus(sessionId, currentAttempt + 1), 2000);
    } catch (error) {
      
      setStatus('error');
      toast.error('Failed to verify payment status');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-12 text-center">
          <div className="flex items-center justify-center mb-6">
            <Code className="w-8 h-8 text-violet-600" />
            <span className="text-2xl font-bold text-slate-900 ml-2">CodeITT</span>
          </div>

          {status === 'checking' && (
            <div data-testid="payment-checking">
              <Loader className="w-20 h-20 text-violet-600 mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-bold text-slate-900 mb-4">Verifying Payment...</h1>
              <p className="text-slate-600 mb-4">Please wait while we confirm your payment.</p>
              <p className="text-sm text-slate-500">Attempt {attempts + 1} of {maxAttempts}</p>
            </div>
          )}

          {status === 'success' && paymentDetails && (
            <div data-testid="payment-success">
              <CheckCircle className="w-20 h-20 text-emerald-600 mx-auto mb-6" />
              <h1 className="text-4xl font-bold text-slate-900 mb-4">Payment Successful!</h1>
              <p className="text-lg text-slate-600 mb-8">
                Thank you for subscribing to <strong>{paymentDetails.package_name}</strong>
              </p>
              
              <div className="bg-emerald-50 rounded-xl p-6 mb-8 text-left">
                <h3 className="font-semibold text-slate-900 mb-4">Payment Details:</h3>
                <div className="space-y-2 text-slate-700">
                  <div className="flex justify-between">
                    <span>Package:</span>
                    <span className="font-medium">{paymentDetails.package_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Amount Paid:</span>
                    <span className="font-medium text-emerald-600">
                      ${paymentDetails.amount} {paymentDetails.currency?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Status:</span>
                    <span className="font-medium text-emerald-600">Paid</span>
                  </div>
                </div>
              </div>

              <div className="bg-violet-50 rounded-lg p-4 mb-8">
                <p className="text-sm text-slate-700">
                  <strong>What's Next?</strong> You can now schedule your classes from your dashboard. A confirmation email has been sent to your registered email address.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/student/dashboard')}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white h-12 rounded-full font-medium"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div data-testid="payment-error">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">⚠️</span>
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-4">Payment Verification Failed</h1>
              <p className="text-slate-600 mb-8">
                We couldn't verify your payment status. Please check your email for confirmation or contact support.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/student/dashboard')}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white h-12 rounded-full font-medium"
                >
                  Go to Dashboard
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/payment')}
                  className="w-full h-12 rounded-full font-medium"
                >
                  Try Again
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
